import {
  CoverageClassCode,
  type CoverageIdentifiers,
  createCodeableConcept,
  createMapWithId,
  getPlan,
  getReferenceType,
  valsOrNone,
} from '@quromedical/fhir-common';
import { first } from '@quromedical/utils';
import type { Coverage } from 'fhir/r4';
import { getValidReference } from '../mappings/reference';
import {
  isGeneralInsurance,
  mapFhirToModelOrganization,
} from '../organization/mapping';

import type { MedicalAidPerson, PatientMedicalAidMembership } from './schema';

type AllowedSubscriber = 'Patient' | 'RelatedPerson';

const allowedSubscriberTypes: AllowedSubscriber[] = [
  'Patient',
  'RelatedPerson',
];

/**
 * Get the type of the resource to be used for fetching the subscriber
 * @param coverage
 * @returns the type of the subscriber
 */
export const getSubscriberType = (
  coverage?: Coverage,
): AllowedSubscriber | undefined => {
  if (!coverage) {
    return undefined;
  }

  const subscriberType = getReferenceType(coverage.subscriber);

  const resolvedType = allowedSubscriberTypes.find(
    (resourceType) => resourceType === subscriberType,
  );

  return resolvedType;
};

export const createCoverageClasses = (
  plan?: string,
  option?: string,
): fhir4.CoverageClass[] | undefined => {
  const planClass: fhir4.CoverageClass | undefined = plan
    ? {
        type: createCodeableConcept(CoverageClassCode.Plan),
        value: plan,
      }
    : undefined;

  const optionClass: fhir4.CoverageClass | undefined = option
    ? {
        type: createCodeableConcept(CoverageClassCode.SubPlan),
        value: option,
      }
    : undefined;

  return valsOrNone([planClass, optionClass]);
};

export const mapPatientMembershipToCoverage = (
  membership: PatientMedicalAidMembership,
): fhir4.Coverage => {
  const {
    dependantCode,
    mainMember,
    memberNumber,
    patient,
    scheme,
    status,
    plan,
  } = membership;

  const classes = createCoverageClasses(plan);

  return {
    resourceType: 'Coverage',
    status,
    class: classes,
    beneficiary: patient,
    subscriber: mainMember,
    payor: [scheme],
    subscriberId: memberNumber,
    dependent: dependantCode,
  };
};

export const mapCoverageToPatientMembership = (
  coverage: fhir4.Coverage,
): Partial<PatientMedicalAidMembership> => {
  const { status, beneficiary, subscriber, payor, subscriberId, dependent } =
    coverage;

  return {
    status,
    dependantCode: dependent,
    memberNumber: subscriberId,
    mainMember: getValidReference(subscriber),
    patient: getValidReference(beneficiary),
    scheme: getValidReference(first(payor)),
    plan: getPlan(coverage)?.value,
  };
};

export const mapCoverageToPatientMembershipWithId = createMapWithId(
  mapCoverageToPatientMembership,
);

export const mapCoverageToIdentifiers = (
  coverage: fhir4.Coverage,
  scheme: fhir4.Organization,
): CoverageIdentifiers | undefined => {
  const { dependantCode, memberNumber } =
    mapCoverageToPatientMembership(coverage);
  const { general } = mapFhirToModelOrganization(scheme);

  const isInsuranceOrg = isGeneralInsurance(general);
  if (!isInsuranceOrg) {
    return undefined;
  }

  const { schemeCode } = general;

  const hasAllIdentifiers = schemeCode && dependantCode && memberNumber;

  if (!hasAllIdentifiers) {
    return undefined;
  }

  return {
    dependantCode,
    schemeCode,
    subscriberId: memberNumber,
  };
};

type PatientMedicalAidMembershipIdentifiers = Pick<
  PatientMedicalAidMembership,
  'memberNumber' | 'dependantCode' | 'plan'
>;

export const getMedicalAidIdentifiers = (
  membership: Partial<MedicalAidPerson>,
): PatientMedicalAidMembershipIdentifiers | undefined => {
  const { medicalAid, dependantCode } = membership.medicalAidMembership || {};

  const { membershipNumber, plan } = medicalAid || {};

  const hasMedicalAidIdentifiers = dependantCode && plan && membershipNumber;

  if (!hasMedicalAidIdentifiers) {
    return undefined;
  }

  return {
    dependantCode,
    plan,
    memberNumber: membershipNumber,
  };
};
